import { useState, type ReactNode } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from '@afrilink/ui';

/**
 * ConfirmDialog — the design-system answer to `window.confirm`. (F-27)
 *
 * WHY THIS EXISTS
 * Eight destructive or outward-facing actions in the admin console were gated
 * by a native browser dialog: demoting a staff member, deleting an expense,
 * firing a cron, resending a customer email, marking a domain internal. A
 * native confirm carries none of the product's chrome, cannot be styled to
 * signal severity, renders `\n\n` as literal text, and — the part that matters
 * — a browser will offer to suppress it after one use, after which it returns
 * `false` silently. That fails safe here, but it also means the control simply
 * stops appearing.
 *
 * The console already had the right pattern in one place: the overdue-reminders
 * flow on the Invoices page. This generalises it so the next destructive action
 * does not have to reinvent it, and so the seven remaining sites stop being
 * seven slightly different experiences.
 *
 * `tone="danger"` is for things that destroy or spend; `default` for things
 * that are merely worth pausing over. `confirmWord` adds the strongest friction
 * available — typing a value — for the small number of actions where a click is
 * not enough deliberation.
 */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** One or two sentences. Say what will happen, in the user's terms. */
  description?: ReactNode;
  /** Detail worth showing but not worth putting in the description. */
  children?: ReactNode;
  /** The button label. A verb naming the outcome — "Delete expense", never "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** When set, the confirm button stays disabled until the user types this. */
  confirmWord?: string;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  confirmWord,
  busy = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const wordSatisfied = !confirmWord || typed.trim() === confirmWord;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        if (!v) setTyped('');
        onOpenChange(v);
      }}
      title={title}
      description={typeof description === 'string' ? description : undefined}
    >
      <ModalBody>
        {typeof description !== 'string' && description}
        {children}
        {confirmWord && (
          <label className="mt-4 block text-sm">
            <span className="text-foreground-soft">
              Type <code className="rounded bg-muted px-1 font-mono">{confirmWord}</code> to
              confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
              autoComplete="off"
              className="mt-1 h-10 w-full rounded border border-input bg-card px-3 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </label>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={tone === 'danger' ? 'destructive' : 'primary'}
          onClick={() => void onConfirm()}
          disabled={busy || !wordSatisfied}
          loading={busy}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
